<script lang="ts">
import type { StyleValue } from 'vue';
import type { NxDatePreset } from './datePresets';

export type DateInputV2Props = {
  id?: string;
  /** Display format for the input value. Tokens: `YYYY` (year), `MM` (month), `DD` (day) -> e.g. `'DD.MM.YYYY'`. Default to locale format. */
  format?: string;
  placeholder?: string;
  disabled?: boolean;
  /** BCP-47 locale used for the calendar header and weekday names. Defaults to the i18n injection locale. */
  locale?: string;
  /** ISO `YYYY-MM-DD` lower bound (inclusive). */
  minValue?: string;
  /** ISO `YYYY-MM-DD` upper bound (inclusive). */
  maxValue?: string;
  wrapperStyle?: StyleValue;
  height?: InputHeight;
  /**
   * When `true`, the calendar icon is hidden until the field is hovered or
   * focused, then fades in — its space stays reserved, so nothing shifts.
   * Useful in dense layouts (e.g. table cells) to cut visual noise. Opt-in;
   * defaults to `false` (icon always shown).
   */
  revealIcon?: boolean;
  /** Tint Saturdays and Sundays in the calendar. */
  markWeekends?: boolean;
  /**
   * Public holidays to mark in the calendar, ISO `YYYY-MM-DD` → name. The name
   * is the day's tooltip and part of its accessible name. The kit knows no
   * holidays itself — which days count depends on the company's region, so the
   * caller supplies them.
   */
  holidays?: Readonly<Record<string, string>>;
  /**
   * One-click days offered as chips under the field („Heute“, „Morgen“, …), see
   * `defaultDatePresets()`. A chip shows as selected while the field holds its
   * day. Unset or empty: no chip row.
   */
  datePresets?: readonly NxDatePreset[];
  /** Accessible name of the preset chip group. */
  datePresetsLabel?: string;
  /**
   * Reference day (ISO `YYYY-MM-DD`) for the presets, the relative suffix, the
   * calendar's today mark and shorthand entries. Defaults to the current day.
   */
  today?: string;
  /**
   * Append the distance to today to the shown date („08.07.2026 · morgen“).
   * Only while the field is not being edited; the typed text stays plain.
   */
  relative?: boolean;
  /**
   * Warn under the field while the value is a Saturday, a Sunday or one of
   * `holidays`, with a button that moves it to the next working day.
   */
  warnNonWorkingDay?: boolean;
  /** Reason shown for a Saturday or Sunday (a holiday shows its own name). */
  weekendLabel?: string;
  /** Text after the reason („Wochenende – …“). */
  nonWorkingDayHint?: string;
  /** Label of the button that moves the value to the next working day. */
  nextWorkingDayLabel?: string;
};

function getLocalDateFormat(locale: string) {
  const parts = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(2000, 0, 2));
  const partReplacements = { year: 'YYYY', month: 'MM', day: 'DD' } as const;
  const allowedParts = [...Object.keys(partReplacements), 'literal'];
  return parts
    .filter((p) => allowedParts.includes(p.type))
    .map((p) =>
      p.type === 'literal' ? p.value : partReplacements[p.type as keyof typeof partReplacements]
    )
    .join('');
}
</script>

<script setup lang="ts">
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@blpde/nx-icons';
import { computed, inject, onUnmounted, ref, useId, useTemplateRef, watch } from 'vue';
import { NxBanner } from '../Banner';
import { NxButton } from '../Button';
import NxChip from '../Chip/NxChip.vue';
import NxChipGroup from '../Chip/NxChipGroup.vue';
import { NxDropdown } from '../Dropdown';
import { daysBetween, nextWorkingDay, nonWorkingDayReason, relativeDayLabel } from './datePresets';
import { formatPlaceholder } from './formatPlaceholder';
import {
  FONT_SIZE_LOOKUP,
  i18nInjectionKey,
  InputHeight,
  labelInjectionKey,
  loadingStateInjectionKey,
} from './internal';
import NxTextInput from './NxTextInput.vue';

const props = withDefaults(defineProps<DateInputV2Props>(), {
  format: undefined,
  id: undefined,
  placeholder: undefined,
  locale: undefined,
  minValue: undefined,
  maxValue: undefined,
  wrapperStyle: undefined,
  icons: undefined,
  height: undefined,
  revealIcon: false,
  markWeekends: false,
  holidays: undefined,
  datePresets: undefined,
  datePresetsLabel: 'Datum-Schnellwahl',
  today: undefined,
  relative: false,
  warnNonWorkingDay: false,
  weekendLabel: 'Wochenende',
  nonWorkingDayHint: 'an diesem Tag arbeitet niemand.',
  nextWorkingDayLabel: 'Nächster Werktag',
});

const valueModel = defineModel<string | null>({ required: true });

const i18nInjection = inject(i18nInjectionKey)!;
const loadingState = inject(loadingStateInjectionKey, null);

let elementId = props.id;
const labelInjection = inject(labelInjectionKey, null);
if (labelInjection && !labelInjection.for.value) {
  elementId ??= useId();
  labelInjection.setFor(elementId);
}

const disabled = computed(() => !!(props.disabled || loadingState?.status.value?.disabled));
const locale = computed(() => props.locale ?? i18nInjection.locale.value);
const formatInternal = computed(() => props.format ?? getLocalDateFormat(locale.value));
const placeholderInternal = computed(
  () => props.placeholder ?? formatPlaceholder(formatInternal.value, locale.value)
);

type DateParts = { year: number; month: number; day: number };

const parseIso = (iso: string | null | undefined): DateParts | null => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const parts = { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  return isValidDate(parts) ? parts : null;
};

const toIso = (p: DateParts): string =>
  `${String(p.year).padStart(4, '0')}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;

/** The reference day: the `today` prop when it holds a valid day, the current day otherwise. */
const todayParts = (): DateParts => {
  const fixed = parseIso(props.today);
  if (fixed) return fixed;
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
};

const daysInMonth = (year: number, month: number): number => new Date(year, month, 0).getDate();

const isValidDate = (p: DateParts): boolean => {
  if (p.year < 1 || p.month < 1 || p.month > 12 || p.day < 1) return false;
  return p.day <= daysInMonth(p.year, p.month);
};

const formatDisplay = (p: DateParts | null): string => {
  if (!p) return '';
  return formatInternal.value
    .replace(/YYYY/g, String(p.year).padStart(4, '0'))
    .replace(/MM/g, String(p.month).padStart(2, '0'))
    .replace(/DD/g, String(p.day).padStart(2, '0'));
};

/**
 * Parses freeform user input into an ISO date string.
 * - `''` → `null`
 * - `'0'` → today
 * - 1-2 digits → day, current month + year
 * - 3-4 digits → day + month, current year
 * - 5-6 digits → day + month + year (year with 2000 prefix)
 * - 7-8 digits → day + month + year (year with 2000 prefix if `formatInternal` starts with `'Y'`)
 *
 * Assumption: for uneven lengths we assume the user omitted a leading zero
 *
 * Returns `undefined` if input is non-empty but cannot be parsed.
 */
const parseInput = (raw: string): string | null | undefined => {
  const trimmed = raw.trim();
  // special cases
  if (!trimmed) return null;
  if (trimmed === '0') return toIso(todayParts());

  const now = todayParts();
  let day = now.day;
  let month = now.month;
  let year = now.year;
  const assign = (token: 'D' | 'M' | 'Y', raw: string) => {
    const nr = Number(raw);
    switch (token) {
      case 'D':
        day = nr;
        break;
      case 'M':
        month = nr;
        break;
      case 'Y':
        year = raw.length <= 2 ? 2000 + nr : nr;
        break;
    }
  };

  const order = ['D', 'M', 'Y'].sort(
    (a, b) => formatInternal.value.indexOf(a) - formatInternal.value.indexOf(b)
  ) as ('D' | 'M' | 'Y')[];

  // contains separators -> split (so 1.1 becomes 01.01 not 11.xx)
  const separators = new Set(formatInternal.value.replaceAll(/Y|M|D/g, ''));
  if ([...separators].some((s) => trimmed.includes(s))) {
    const sepClass = [...separators].map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('');
    const chunks = trimmed.split(new RegExp(`[${sepClass}]`)).filter((v) => v.length > 0);
    if (chunks.some((c) => !/^\d+$/.test(c))) return undefined;
    const orderNoY = order.filter((o) => o !== 'Y') as ('D' | 'M')[];
    switch (chunks.length) {
      case 1:
        assign('D', chunks[0]);
        break;
      case 2:
        assign(orderNoY[0], chunks[0]);
        assign(orderNoY[1], chunks[1]);
        break;
      case 3:
        order.forEach((t, i) => assign(t, chunks[i]));
        break;
      default:
        return undefined;
    }
    const parts = { year, month, day };
    if (!isValidDate(parts)) return undefined;
    return toIso(parts);
  }
  // else (no separators -> strip non digits)
  let digits = trimmed.replace(/\D/g, '');
  if (!digits) return undefined;
  // assumption: leading zero was omitted
  if (digits.length % 2 === 1) {
    digits = `0${digits}`;
  }
  const dayBeforeMonth = formatInternal.value.indexOf('D') < formatInternal.value.indexOf('M');
  switch (digits.length) {
    case 2:
      assign('D', digits);
      break;
    case 4:
      // 2-2 chunks
      assign('D', dayBeforeMonth ? digits.slice(0, 2) : digits.slice(2, 4));
      assign('M', dayBeforeMonth ? digits.slice(2, 4) : digits.slice(0, 2));
      break;
    case 6:
      // 2-2-2 chunks
      order.forEach((v, i) => {
        assign(v, digits.slice(i * 2, (i + 1) * 2));
      });
      break;
    case 8:
      // 2-2-4 chunks (in any order)
      let offset = 0;
      order.forEach((v) => {
        const chunkSize = v === 'Y' ? 4 : 2;
        const chunk = digits.slice(offset, offset + chunkSize);
        assign(v, chunk);
        offset += chunkSize;
      });
      break;
    default:
      return undefined;
  }
  const parts = { year, month, day };
  if (!isValidDate(parts)) return undefined;
  return toIso(parts);
};

const focused = ref(false);
const rawInput = ref('');
/**
 * The typed text could not be read as a day. It stays set — and the typed text
 * stays in the field, focused or not — until the user corrects it: a valid entry
 * or an emptied field, a calendar or chip pick, Escape, or a new value from the
 * host. A transient flash followed by the stored value silently threw away what
 * the user typed (BLP/nx#2833).
 */
const parseError = ref(false);

/** The model as a plain ISO day, `null` when empty or unparseable. */
const currentIso = computed(() => {
  const p = parseIso(valueModel.value);
  return p ? toIso(p) : null;
});
const referenceIso = computed(() => toIso(todayParts()));

/**
 * Write a day into the model, but only when it is a different calendar day.
 *
 * Compares against `currentIso`, not the raw model: the model may carry the day
 * as an RFC 3339 timestamp (`2007-01-01T00:00:00Z`, as several ERP read paths
 * deliver dates), which never equals the plain `YYYY-MM-DD` this field produces.
 * The raw comparison turned a mere click into a date cell and out again into a
 * write (BLP/nx#2190).
 */
const writeDay = (day: string | null) => {
  parseError.value = false;
  if (day !== currentIso.value) valueModel.value = day;
};

/** „heute“, „in 3 Tagen“, … — only with `relative`. */
const relativeSuffix = computed(() => {
  if (!props.relative || !currentIso.value) return '';
  return relativeDayLabel(daysBetween(referenceIso.value, currentIso.value), locale.value);
});

const displayValue = computed<string>({
  get() {
    // An invalid entry stays visible after blur, so the user sees what to fix.
    if (focused.value || parseError.value) return rawInput.value;
    const text = formatDisplay(parseIso(valueModel.value));
    return text && relativeSuffix.value ? `${text} · ${relativeSuffix.value}` : text;
  },
  set(v) {
    rawInput.value = v;
  },
});

const onFocus = () => {
  focused.value = true;
  // Re-entering a field that holds an invalid entry continues editing that
  // entry instead of replacing it with the stored value.
  if (!parseError.value) rawInput.value = formatDisplay(parseIso(valueModel.value));
};

/**
 * Parse what the user typed and push it into the model. Shared by blur and
 * Enter so the parse-error handling below exists exactly once.
 *
 * Returns the parsed value, or `undefined` when the entry was unparseable —
 * deliberately the value and not just a flag: the Enter handler needs it to
 * normalise the still-focused field's text, and reading it back from
 * `valueModel` instead would depend on the parent's v-model round-trip having
 * completed, which it need not have.
 */
const commit = (): string | null | undefined => {
  const parsed = parseInput(rawInput.value);
  if (parsed === undefined) {
    parseError.value = true;
    return undefined;
  }
  parseError.value = false;
  writeDay(parsed);
  return parsed;
};

const onBlur = () => {
  focused.value = false;
  commit();
};

/**
 * Enter confirms the typed value without leaving the field.
 *
 * Needed because this input does NOT go through `v-model.lazy` like the other
 * NxInput-based fields: the inner v-model only writes `rawInput`, and the model
 * was written in `onBlur` alone — so Enter did nothing at all and users saw
 * their input silently dropped (BLP/nx#762). The other field types inherit
 * Enter for free, because `.lazy` binds the native `change` event, which the
 * browser fires on Enter.
 *
 * The field keeps focus afterwards, so `rawInput` is refreshed from the model:
 * a shorthand entry ("3009") is then shown normalised ("30.09.2026"), the same
 * as after leaving the field. On an unparseable value nothing is rewritten, so
 * the user keeps their text next to the error hint and can correct it.
 */
const onEnter = () => {
  const parsed = commit();
  if (parsed !== undefined) {
    rawInput.value = formatDisplay(parseIso(parsed));
  }
};

/**
 * Escape discards the typed text and shows the stored value again.
 *
 * Only while the calendar is CLOSED: with an open calendar Escape already means
 * "close the calendar" (the document-level handler below). That handler only
 * calls preventDefault, which does not stop propagation, so both would run —
 * closing the calendar must not also throw away what the user typed.
 */
const onEscape = () => {
  if (isOpen.value) return;
  parseError.value = false;
  rawInput.value = formatDisplay(parseIso(valueModel.value));
};

const isOpen = ref(false);
const containerRef = useTemplateRef<HTMLElement>('container-ref');

const initialView = parseIso(valueModel.value) ?? todayParts();
const viewYear = ref(initialView.year);
const viewMonth = ref(initialView.month);

watch(
  () => valueModel.value,
  (v, previous) => {
    // A new value from the host (a reset, a reload) replaces a pending invalid
    // entry while the user is not editing it.
    if (!focused.value) parseError.value = false;
    // A new value from the host while the field has focus but the user has not
    // typed yet (the text is still the value it showed) is shown too. Otherwise
    // a value the host proposes for a field the user just tabbed into stays
    // invisible, and leaving the field writes the old, empty text back over it
    // (BLP/nx#3198: the posting date proposed from the document date).
    if (
      focused.value &&
      !parseError.value &&
      rawInput.value === formatDisplay(parseIso(previous))
    ) {
      rawInput.value = formatDisplay(parseIso(v));
    }
    const p = parseIso(v);
    if (p) {
      viewYear.value = p.year;
      viewMonth.value = p.month;
    }
  }
);

const openCalendar = () => {
  if (disabled.value) return;
  const p = parseIso(valueModel.value) ?? todayParts();
  viewYear.value = p.year;
  viewMonth.value = p.month;
  isOpen.value = true;
};

const closeCalendar = () => {
  isOpen.value = false;
};

const prevMonth = () => {
  if (viewMonth.value === 1) {
    viewYear.value -= 1;
    viewMonth.value = 12;
  } else {
    viewMonth.value -= 1;
  }
};

const nextMonth = () => {
  if (viewMonth.value === 12) {
    viewYear.value += 1;
    viewMonth.value = 1;
  } else {
    viewMonth.value += 1;
  }
};

/** One week per row, six rows — enough for every month layout. */
const COLS = 7;
const ROWS = 6;
const CELL_COUNT = COLS * ROWS;

type CalendarCell = {
  iso: string;
  day: number;
  outside: boolean;
  today: boolean;
  selected: boolean;
};

const calendarCells = computed<CalendarCell[]>(() => {
  const year = viewYear.value;
  const month = viewMonth.value;
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % COLS;
  const daysCur = daysInMonth(year, month);
  const cells: CalendarCell[] = [];
  const sel = parseIso(valueModel.value);
  const td = todayParts();

  const prevYear = month === 1 ? year - 1 : year;
  const prevMonthNum = month === 1 ? 12 : month - 1;
  const prevDays = daysInMonth(prevYear, prevMonthNum);
  for (let i = 0; i < firstWeekday; i++) {
    const d = prevDays - firstWeekday + 1 + i;
    cells.push({
      iso: toIso({ year: prevYear, month: prevMonthNum, day: d }),
      day: d,
      outside: true,
      today: td.year === prevYear && td.month === prevMonthNum && td.day === d,
      selected: !!sel && sel.year === prevYear && sel.month === prevMonthNum && sel.day === d,
    });
  }

  for (let d = 1; d <= daysCur; d++) {
    cells.push({
      iso: toIso({ year, month, day: d }),
      day: d,
      outside: false,
      today: td.year === year && td.month === month && td.day === d,
      selected: !!sel && sel.year === year && sel.month === month && sel.day === d,
    });
  }

  const nextYear = month === 12 ? year + 1 : year;
  const nextMonthNum = month === 12 ? 1 : month + 1;
  let nd = 1;
  while (cells.length < CELL_COUNT) {
    cells.push({
      iso: toIso({ year: nextYear, month: nextMonthNum, day: nd }),
      day: nd,
      outside: true,
      today: td.year === nextYear && td.month === nextMonthNum && td.day === nd,
      selected: !!sel && sel.year === nextYear && sel.month === nextMonthNum && sel.day === nd,
    });
    nd++;
  }

  return cells;
});

const isCellDisabled = (iso: string): boolean => {
  if (props.minValue && iso < props.minValue) return true;
  if (props.maxValue && iso > props.maxValue) return true;
  return false;
};

const weekDayNames = computed<string[]>(() => {
  const fmt = new Intl.DateTimeFormat(locale.value, { weekday: 'short' });
  // 2024-01-01 is a Monday — week starts Monday.
  const monday = new Date(2024, 0, 1);
  return Array.from({ length: COLS }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return fmt.format(d);
  });
});

const headingText = computed(() => {
  const fmt = new Intl.DateTimeFormat(locale.value, { month: 'long', year: 'numeric' });
  return fmt.format(new Date(viewYear.value, viewMonth.value - 1, 1));
});

const selectCell = (cell: CalendarCell) => {
  if (isCellDisabled(cell.iso)) return;
  writeDay(cell.iso);
  rawInput.value = formatDisplay(parseIso(cell.iso));
  closeCalendar();
};

const presetDay = (preset: NxDatePreset): string | null => preset.date(referenceIso.value);

const presetSelected = (preset: NxDatePreset): boolean => presetDay(preset) === currentIso.value;

/** A preset day outside `minValue`/`maxValue` is offered disabled; clearing always works. */
const presetDisabled = (preset: NxDatePreset): boolean => {
  if (disabled.value) return true;
  const day = presetDay(preset);
  return day !== null && isCellDisabled(day);
};

const applyPreset = (preset: NxDatePreset) => {
  const day = presetDay(preset);
  writeDay(day);
  rawInput.value = formatDisplay(parseIso(day));
  closeCalendar();
};

/** Holiday name or weekend label while the value is no working day — only with `warnNonWorkingDay`. */
const nonWorkingReason = computed(() =>
  props.warnNonWorkingDay && currentIso.value
    ? nonWorkingDayReason(currentIso.value, props.holidays, props.weekendLabel)
    : null
);

const nextWorkingIso = computed(() =>
  nonWorkingReason.value && currentIso.value
    ? nextWorkingDay(currentIso.value, props.holidays)
    : null
);

const nextWorkingDisabled = computed(
  () => disabled.value || !nextWorkingIso.value || isCellDisabled(nextWorkingIso.value)
);

const moveToNextWorkingDay = () => {
  const next = nextWorkingIso.value;
  if (!next) return;
  closeCalendar();
  // The banner — and with it the focused button — goes away with the
  // non-working day, so hand focus to the field instead of dropping it on the
  // page. Before the model write: focusing copies the model into the edit text,
  // and the parent's v-model round trip has not happened yet.
  containerRef.value?.querySelector<HTMLInputElement>('input')?.focus();
  parseError.value = false;
  valueModel.value = next;
  rawInput.value = formatDisplay(parseIso(next));
};

const CELL_SELECTOR = '.calendar-cell-trigger';

const queryCell = (index: number): HTMLButtonElement | null =>
  containerRef.value?.querySelector<HTMLButtonElement>(
    `${CELL_SELECTOR}[data-cell-index="${index}"]`
  ) ?? null;

const queryNav = (side: 'prev' | 'next'): HTMLButtonElement | null =>
  containerRef.value?.querySelector<HTMLButtonElement>(`.calendar-nav-${side}`) ?? null;

const focusEl = (el: HTMLButtonElement | null) => {
  if (!el || el.disabled) return;
  el.focus();
};

/**
 * Enter the grid from a month-nav caret: the previous-month caret drops onto the
 * first day of the top row, the next-month one onto its last day. Disabled cells
 * (min/max bounds) are skipped inwards, so a caret is never a dead end.
 */
const focusTopRowFrom = (side: 'prev' | 'next') => {
  const order = Array.from({ length: COLS }, (_, i) => (side === 'prev' ? i : COLS - 1 - i));
  for (const index of order) {
    const el = queryCell(index);
    if (el && !el.disabled) {
      el.focus();
      return;
    }
  }
};

const onCellKeydown = (e: KeyboardEvent, target: HTMLElement) => {
  const idxAttr = target.getAttribute('data-cell-index');
  if (idxAttr === null) return;
  const idx = Number(idxAttr);
  const col = idx % COLS;
  let next: number | null = null;
  switch (e.key) {
    case 'ArrowLeft':
      if (col > 0) next = idx - 1;
      break;
    case 'ArrowRight':
      if (col < COLS - 1) next = idx + 1;
      break;
    case 'ArrowUp':
      // The top row leaves the grid upwards onto the month-nav carets: the left
      // half of the week (Mon-Wed) to the previous-month caret, the right half
      // (Thu-Sun) to the next-month one.
      if (idx < COLS) focusEl(queryNav(col <= 2 ? 'prev' : 'next'));
      else next = idx - COLS;
      break;
    case 'ArrowDown':
      if (idx < (ROWS - 1) * COLS) next = idx + COLS;
      break;
    default:
      return;
  }
  e.preventDefault();
  e.stopPropagation();
  if (next !== null) focusEl(queryCell(next));
};

const onNavKeydown = (e: KeyboardEvent, side: 'prev' | 'next') => {
  switch (e.key) {
    case 'ArrowDown':
      focusTopRowFrom(side);
      break;
    case 'ArrowLeft':
      focusEl(queryNav('prev'));
      break;
    case 'ArrowRight':
      focusEl(queryNav('next'));
      break;
    default:
      return;
  }
  e.preventDefault();
  e.stopPropagation();
};

/**
 * Page the calendar from a month-nav caret.
 *
 * The day buttons are keyed by their date, so paging unmounts the focused day
 * as soon as it leaves the shown grid, and the caret's `@mousedown.prevent`
 * keeps the focus from moving to the caret on its own. Focus then fell back to
 * `<body>`, which a host grid reads as "left the cell": it ended the edit and
 * closed the calendar, or the calendar lost the keyboard and the carets went
 * dead (BLP/nx#2932). So focus inside the calendar moves to the clicked caret
 * before the grid changes — from a day, and from the other caret, so the focus
 * ring and Enter follow the caret last used; focus in the text field stays where
 * it is.
 */
const pageMonth = (side: 'prev' | 'next') => {
  const active = document.activeElement;
  // This field's own calendar only: a host element may carry the same class.
  const calendar = containerRef.value?.querySelector('.calendar');
  if (active && calendar?.contains(active)) focusEl(queryNav(side));
  if (side === 'prev') prevMonth();
  else nextMonth();
};

/**
 * Arrow-key navigation inside the open calendar.
 *
 * Bound on the calendar root rather than the day grid so the month-nav carets
 * take part in it — they are siblings of the grid, so a handler on the grid
 * itself would never see their key events.
 */
const onCalendarKeydown = (e: KeyboardEvent) => {
  const target = e.target as HTMLElement | null;
  if (!target) return;
  // Enter and Space press the calendar's own buttons — a caret pages, a day is
  // picked. A host grid binds its cell keys on this field's root and reads Enter
  // as "commit and move on": it cancelled the press and left the cell
  // (BLP/nx#2932). The press itself stays native, so nothing is prevented here.
  if (e.key === 'Enter' || e.key === ' ') {
    e.stopPropagation();
    return;
  }
  if (target.classList.contains('calendar-cell-trigger')) {
    onCellKeydown(e, target);
    return;
  }
  if (target.classList.contains('calendar-nav-prev')) onNavKeydown(e, 'prev');
  else if (target.classList.contains('calendar-nav-next')) onNavKeydown(e, 'next');
};

const onDocumentMousedown = (e: MouseEvent) => {
  const target = e.target as Node | null;
  if (!target) return;
  if (containerRef.value?.contains(target)) return;
  closeCalendar();
};

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isOpen.value) {
    e.preventDefault();
    closeCalendar();
  }
};

/**
 * Move focus into the grid when the calendar opens: onto the selected day when
 * the model holds one, otherwise onto today. The last fallback (first selectable
 * cell) keeps focus from staying behind in the text field, where the arrow keys
 * below would never see it.
 */
const focusInitialCell = () => {
  const root = containerRef.value;
  if (!root) return;
  const target =
    root.querySelector<HTMLButtonElement>(`${CELL_SELECTOR}[data-selected]:not(:disabled)`) ??
    root.querySelector<HTMLButtonElement>(`${CELL_SELECTOR}[data-today]:not(:disabled)`) ??
    root.querySelector<HTMLButtonElement>(`${CELL_SELECTOR}:not(:disabled)`);
  target?.focus({ focusVisible: true } as FocusOptions);
};

watch(isOpen, (open) => {
  if (open) {
    setTimeout(() => {
      document.addEventListener('mousedown', onDocumentMousedown, true);
      document.addEventListener('keydown', onKeydown, true);
      focusInitialCell();
    }, 0);
  } else {
    document.removeEventListener('mousedown', onDocumentMousedown, true);
    document.removeEventListener('keydown', onKeydown, true);
  }
});

onUnmounted(() => {
  document.removeEventListener('mousedown', onDocumentMousedown, true);
  document.removeEventListener('keydown', onKeydown, true);
});
</script>

<template>
  <div ref="container-ref" class="nx-date-input-v2" :class="{ 'parse-error': parseError }">
    <NxDropdown :is-open="isOpen" align="end" @close="closeCalendar">
      <template #trigger>
        <NxTextInput
          :id="elementId"
          v-model="displayValue"
          :escape-restores="false"
          :icons="[
            {
              align: 'end',
              component: CalendarIcon,
              filled: true,
              // Always a functional icon: when disabled the handler is stripped,
              // but `deco: false` keeps it an (inert) action button instead of
              // flipping to a decorative span — so reveal-on-interaction and the
              // disabled styling still apply.
              deco: false,
              color: disabled
                ? 'var(--nxt-border-strong)'
                : isOpen
                  ? 'var(--nxt-brand-bg)'
                  : undefined,
              onClick: disabled ? undefined : () => (isOpen ? closeCalendar() : openCalendar()),
              revealOnInteraction: props.revealIcon,
            },
          ]"
          :wrapper-style="props.wrapperStyle"
          :height="props.height"
          :placeholder="placeholderInternal"
          :disabled="disabled"
          :data-focus-visible="isOpen ? true : undefined"
          :aria-invalid="parseError ? 'true' : undefined"
          @focus="onFocus"
          @blur="onBlur"
          @keydown.enter="onEnter"
          @keydown.esc="onEscape"
        />
      </template>
      <div
        class="calendar"
        :style="`--font-size: ${FONT_SIZE_LOOKUP[props.height ?? 's']}`"
        @keydown="onCalendarKeydown"
      >
        <NxButton
          class="calendar-nav-prev"
          width="icon-only"
          variant="ghost"
          height="xs"
          :disabled="disabled"
          @mousedown.prevent
          @click="pageMonth('prev')"
        >
          <ChevronLeftIcon filled />
        </NxButton>
        <div class="calendar-heading">{{ headingText }}</div>
        <NxButton
          class="calendar-nav-next"
          width="icon-only"
          variant="ghost"
          height="xs"
          :disabled="disabled"
          @mousedown.prevent
          @click="pageMonth('next')"
        >
          <ChevronRightIcon filled />
        </NxButton>
        <div class="calendar-body">
          <div v-for="day in weekDayNames" :key="day" class="calendar-head-cell">
            {{ day }}
          </div>
          <template v-for="row in ROWS" :key="`row-${row}`">
            <button
              v-for="(cell, colIdx) in calendarCells.slice((row - 1) * COLS, row * COLS)"
              :key="cell.iso"
              type="button"
              class="calendar-cell-trigger"
              :data-cell-index="(row - 1) * COLS + colIdx"
              :data-selected="cell.selected ? '' : undefined"
              :data-outside-view="cell.outside ? '' : undefined"
              :data-today="cell.today ? '' : undefined"
              :data-disabled="isCellDisabled(cell.iso) ? '' : undefined"
              :data-weekend="props.markWeekends && colIdx >= 5 ? '' : undefined"
              :data-holiday="props.holidays?.[cell.iso] ? '' : undefined"
              :title="props.holidays?.[cell.iso]"
              :aria-label="
                props.holidays?.[cell.iso] ? `${cell.day}, ${props.holidays[cell.iso]}` : undefined
              "
              :disabled="isCellDisabled(cell.iso)"
              @mousedown.prevent
              @click="selectCell(cell)"
            >
              {{ cell.day }}
            </button>
          </template>
        </div>
      </div>
    </NxDropdown>
    <NxChipGroup
      v-if="props.datePresets?.length"
      class="date-presets"
      :aria-label="props.datePresetsLabel"
    >
      <NxChip
        v-for="preset in props.datePresets"
        :key="preset.id"
        size="xs"
        :selected="presetSelected(preset)"
        :aria-pressed="presetSelected(preset)"
        :disabled="presetDisabled(preset)"
        @click="applyPreset(preset)"
      >
        {{ preset.label }}
      </NxChip>
    </NxChipGroup>
    <!-- The live region stays mounted while the warning is enabled, so a
         warning that appears is announced; empty, it takes no space. -->
    <div v-if="props.warnNonWorkingDay" class="non-working-day" aria-live="polite">
      <NxBanner v-if="nonWorkingReason" variant="warning" class="non-working-day-banner">
        <b>{{ nonWorkingReason }}</b>
        {{ `– ${props.nonWorkingDayHint}` }}
        <template #buttons>
          <NxButton
            height="xs"
            variant="outline"
            :disabled="nextWorkingDisabled"
            @click="moveToNextWorkingDay"
          >
            {{ props.nextWorkingDayLabel }}
          </NxButton>
        </template>
      </NxBanner>
    </div>
  </div>
</template>

<style scoped>
.nx-date-input-v2 {
  width: 100%;
}

/* Rows under the field space themselves, so an empty live region adds no gap. */
.date-presets,
.non-working-day-banner {
  margin-block-start: var(--nxt-space-2xs);
}

/* Through `.input-wrapper` so the mark outranks the wrapper's focus border and
   stays visible while the user corrects the entry. */
.nx-date-input-v2.parse-error :deep(.input-wrapper input) {
  /* Stays until the entry is corrected — the same invalid look as the other kit
     fields (NxInlineEdit, NxRecipientInput). */
  border-color: var(--nxt-input-border-invalid);
  background-color: var(--nxt-danger-subtle-bg);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring-invalid);
}

.calendar {
  font-size: var(--font-size);
  border-radius: var(--nxt-radius-md);
  padding: var(--nxt-space-sm);
  background-color: var(--nxt-popover-bg);
  border: solid var(--nxt-hairline) var(--nxt-popover-border);
  box-shadow: var(--nxt-popover-shadow);
  display: grid;
  justify-items: center;
  align-items: center;
  grid-template-columns: repeat(7, 1.1lh);
  grid-template-rows: auto auto repeat(6, 1.1lh);
  gap: calc(
    var(--nxt-size-unit) * 0.0625
  ); /* stylelint-disable-line declaration-property-unit-disallowed-list -- 1px optical nudge */
  width: fit-content;
  margin-left: auto;
  user-select: none;
}

.calendar-heading {
  grid-column: span 5;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: var(--nxt-font-weight-medium);
  color: var(--nxt-fg);
}

.calendar-body {
  display: contents;
}

.calendar-head-cell {
  color: var(--nxt-fg-muted);
  font-weight: var(--nxt-font-weight-bold);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  margin-block: var(--nxt-space-2xs);
  text-transform: capitalize;
}

.calendar-cell-trigger {
  color: var(--nxt-fg);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  white-space: nowrap;
  border-radius: var(--nxt-radius-sm);
  border: var(--nxt-hairline) solid transparent;
  background-color: transparent;
  cursor: pointer;

  /* Before [data-selected]: a picked holiday shows as picked. */
  &[data-weekend] {
    background-color: var(--nxt-surface-sunken-bg);
    color: var(--nxt-fg-muted);
  }
  &[data-holiday] {
    background-color: var(--nxt-danger-subtle-bg);
    color: var(--nxt-danger-subtle-fg);
    font-weight: var(--nxt-font-weight-medium);
  }
  &[data-selected] {
    background-color: var(--nxt-brand-bg);
    color: var(--nxt-brand-fg);
    font-weight: var(--nxt-font-weight-medium);
  }
  &[data-outside-view] {
    color: var(--nxt-fg-subtle);
  }
  &[data-disabled],
  &:disabled {
    cursor: default;
    color: var(--nxt-fg-muted);
    pointer-events: none;
  }
  &[data-today] {
    text-decoration: underline;
  }
  &:hover,
  &:focus-visible {
    outline: 0;
    border: var(--nxt-hairline) solid var(--nxt-brand-bg);
  }

  &[data-selected]:focus-visible {
    box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-brand-fg);
  }
}
</style>
